import { useState } from 'react'
import { Button } from '../../platform/primitives/Button'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { LogoPicker } from './LogoPicker'
import { useDeleteLogo, useUploadLogo, type Merchant } from './useMerchants'
import './merchants.css'

/**
 * Upload and remove a merchant's logo — Task 9, the app's first file upload.
 *
 * **`LogoPicker` picks and validates; `useUploadLogo` does the multipart
 * assembly; this component only joins the two.** The type and size rules live
 * in `logoFile.ts` since #468, because the create form now has a picker of its
 * own and one of the two copies would otherwise drift.
 *
 * **A caller for a merchant that cannot be edited must not render this.**
 * There is no `isGlobal` check inside the component: `MerchantForm.tsx` is
 * the one place that already knows a merchant is global (Task 8) and simply
 * does not mount this field there, the same way it does not mount Save.
 */
export function MerchantLogoField({ merchant }: { merchant: Merchant }) {
  const [error, setError] = useState<string | undefined>(undefined)
  const upload = useUploadLogo()
  const remove = useDeleteLogo()

  const pending = upload.isPending || remove.isPending

  async function onPick(file: File) {
    setError(undefined)
    try {
      // **`{ id, file }`, not a `FormData` built here.** The multipart
      // assembly — the `logo` field name, leaving `Content-Type` to the
      // platform — belongs to `useUploadLogo` itself (its own docblock), so
      // this call site stays a plain object a caller can construct without
      // knowing any of that.
      await upload.mutateAsync({ id: merchant.id, file })
    } catch {
      // The previous logo is untouched on failure — nothing here rewrites
      // `merchant.logo_url`, which is a prop this component never owns, so
      // `MerchantAvatar` below keeps rendering whatever the caller passed in.
      setError('The logo could not be uploaded. Try again in a moment.')
    }
  }

  async function onRemove() {
    setError(undefined)
    try {
      await remove.mutateAsync(merchant.id)
    } catch {
      setError('The logo could not be removed. Try again in a moment.')
    }
  }

  return (
    <div className="merchant-logo">
      <MerchantAvatar merchant={merchant} size="detail" />
      <div className="merchant-logo__controls">
        <LogoPicker
          pending={pending}
          busyLabel="Uploading…"
          onPick={(file) => void onPick(file)}
          onReject={setError}
        />
        {merchant.logo_url !== null && merchant.logo_url !== undefined && (
          <Button
            type="button"
            variant="chip"
            size="form"
            disabled={pending}
            onClick={() => void onRemove()}
          >
            Remove logo
          </Button>
        )}
      </div>
      {error && (
        <span className="merchant-logo__error" role="alert">
          {error}
        </span>
      )}
    </div>
  )
}
